import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Alert, 
  Grid, 
  ToggleButtonGroup, 
  ToggleButton,
  Tabs,
  Tab
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import publisherConfigService from '../services/publisherConfigService';
import { useAuth } from '../contexts/AuthContext';

interface MetricsData {
  ctr: MetricByPlacement;
  impressions: MetricByPlacement;
  clicks: MetricByPlacement;
  revenue: MetricByPlacement;
  engagement: MetricByPlacement;
  timeframe: string;
}

interface MetricByPlacement {
  before: number[];
  after: number[];
  inline: number[];
  labels: string[];
}

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.9)', 
        border: '1px solid #ccc',
        p: 1,
        borderRadius: 1
      }}>
        <Typography variant="body2">{`${label}`}</Typography>
        {payload.map((item: any, index: number) => (
          <Typography 
            key={index} 
            variant="body2" 
            sx={{ color: item.color }}
          >
            {`${item.name}: ${item.value.toFixed(2)}${item.unit || ''}`}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

const AdPlacementMetrics: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [timeframe, setTimeframe] = useState<string>('30days');
  const [metricType, setMetricType] = useState<string>('ctr');

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!user?.publisher_id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const data = await publisherConfigService.getPlacementMetrics(user.publisher_id, timeframe);
        setMetrics(data);
      } catch (err) {
        console.error('Error fetching metrics', err);
        setError('Failed to load performance metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [user?.publisher_id, timeframe]);

  const handleTimeframeChange = (event: React.MouseEvent<HTMLElement>, newTimeframe: string) => {
    if (newTimeframe !== null) {
      setTimeframe(newTimeframe);
    }
  };

  const handleMetricTypeChange = (event: React.SyntheticEvent, newMetricType: string) => {
    setMetricType(newMetricType);
  };

  // Transform metric data for chart
  const prepareChartData = () => {
    if (!metrics) return [];
    
    const metricData = metrics[metricType as keyof MetricsData] as MetricByPlacement;
    if (!metricData) return [];
    
    return metricData.labels.map((label, index) => ({
      name: label,
      before: metricData.before[index],
      after: metricData.after[index],
      inline: metricData.inline[index]
    }));
  };

  // Get unit based on metric type
  const getMetricUnit = () => {
    switch(metricType) {
      case 'ctr':
        return '%';
      case 'revenue':
        return '$';
      default:
        return '';
    }
  };

  // Format Y-axis ticks - modified to always return a string
  const formatYAxis = (value: number): string => {
    if (metricType === 'ctr') {
      return `${value}%`;
    } else if (metricType === 'revenue') {
      return `$${value}`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toString(); // Convert number to string to satisfy TypeScript
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Ad Placement Performance
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(0, 0, 0, 0.87)' }}>
              Time Period
            </Typography>
            <ToggleButtonGroup
              value={timeframe}
              exclusive
              onChange={handleTimeframeChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  color: 'rgba(0, 0, 0, 0.6)',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  '&.Mui-selected': {
                    color: 'rgba(0, 0, 0, 0.87)',
                    backgroundColor: 'rgba(0, 0, 0, 0.08)'
                  }
                }
              }}
            >
              <ToggleButton value="7days">7 Days</ToggleButton>
              <ToggleButton value="30days">30 Days</ToggleButton>
              <ToggleButton value="90days">90 Days</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
        
        <Tabs
          value={metricType}
          onChange={handleMetricTypeChange}
          sx={{ mb: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="CTR" value="ctr" />
          <Tab label="Impressions" value="impressions" />
          <Tab label="Clicks" value="clicks" />
          <Tab label="Revenue" value="revenue" />
          <Tab label="Engagement" value="engagement" />
        </Tabs>
        
        {!metrics ? (
          <Alert severity="info" sx={{ my: 2 }}>
            No performance data available for the selected time period. Data will appear after ads have been served with different placement strategies.
          </Alert>
        ) : (
          <Box sx={{ height: 400, width: '100%' }}>
            <ResponsiveContainer>
              {metricType === 'impressions' || metricType === 'clicks' ? (
                <BarChart data={prepareChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={formatYAxis} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="before" name="Before Response" fill="#8884d8" />
                  <Bar dataKey="after" name="After Response" fill="#82ca9d" />
                  <Bar dataKey="inline" name="Inline" fill="#ffc658" />
                </BarChart>
              ) : (
                <LineChart data={prepareChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={formatYAxis} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="before" 
                    name="Before Response" 
                    stroke="#8884d8" 
                    activeDot={{ r: 8 }}
                    unit={getMetricUnit()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="after" 
                    name="After Response" 
                    stroke="#82ca9d" 
                    unit={getMetricUnit()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="inline" 
                    name="Inline" 
                    stroke="#ffc658" 
                    unit={getMetricUnit()}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </Box>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Compare the performance of different ad placement strategies based on CTR, impressions, clicks, revenue, and user engagement.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AdPlacementMetrics; 